/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Cashback Reserve Modal success component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.12.0
 */

import { useCallback, useEffect } from 'react';

import { Button } from '../../../../../components/common';
import { ImgStatusSuccess } from '../../../../../components/common/Block/Img';
import GTMOperations from '../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import Modal from '../../../../../modules/modal/components/Modal';
import { formatBalanceWithoutTrailingZeros } from '../../../../../utils/formatText';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { CashbackReserveModalStatusStyled } from '../../styled/CashbackReserveModalStyled';
import { ICashbackReserveModal } from './CashbackReserveModal';

export interface ICashbackReserveModalSuccess
    extends Pick<ICashbackReserveModal, 'service' | 'onCloseModal' | 'translation' | 'translationCommon'> {
    isReserveAllChecked: boolean;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    scoreReserved: number;
}

const CashbackReserveModalSuccess = (props: ICashbackReserveModalSuccess) => {
    const {
        isReserveAllChecked,
        onCloseModal,
        pushGtmAnalytic,
        scoreReserved,
        service,
        translation,
        translationCommon,
    } = props;

    const onCloseModalCallback = useCallback(() => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnReserveModalSuccessButton(
                stringFormatParams(translation.success.title, { name: service.title }),
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        onCloseModal();
    }, [onCloseModal, pushGtmAnalytic, service.title, translation.success.title]);

    useEffect(() => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnReserveSuccess(
                stringFormatParams(translation.start.title, { name: service.title }),
                isReserveAllChecked,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [isReserveAllChecked, pushGtmAnalytic, service.title, translation.start.title]);

    return (
        <CashbackReserveModalStatusStyled>
            <ImgStatusSuccess/>
            <Modal.Title className="modal-popup-title">
                {stringFormatParams(translation.success.title, { name: service.title })}
            </Modal.Title>
            <Modal.Text>
                {stringFormatParams(translation.success.subTitle, {
                    amount: `${formatBalanceWithoutTrailingZeros(scoreReserved, 2)} ${translationCommon.currency}`,
                })}
            </Modal.Text>
            <Modal.Control>
                <Button kind="medium" onClick={onCloseModalCallback}>{translation.success.btnRedirect}</Button>
            </Modal.Control>
        </CashbackReserveModalStatusStyled>
    );
};

export default CashbackReserveModalSuccess;
